import {
  DirectChatsResponse,
  InboxQueryParamsEnum,
  JobChat,
  JobChatResponse,
  TodoResponse,
  WorkerInboxIndicators,
} from '@th-types/inbox-reviews.type';
import type {
  FetchNotificationsParams,
  OtherNotificationResponse,
} from '@th-types/worker-notifications.type';
import { privateApi } from '@services/api';
import { buildInboxQueryParams } from '@utils/InboxHelper';
import { isAxiosError } from 'axios';

/** Raw shape returned by the webapp JobChatController for worker job chats. */
interface BackendJobChatResponse {
  chatId: number;
  jobAssignmentInfo?: {
    jobAssignmentId?: number;
    eventName?: string;
    jobId?: number;
    start?: string;
    end?: string;
    position?: string;
    address?: Record<string, unknown>;
    companyName?: string;
    companyLogoUrl?: string;
    profileURLName?: string;
  };
  workerInfo?: {
    workerId?: number;
    workerFullName?: string;
  };
  unreadMessages?: number;
  lastMessageDateTime?: string;
  firstUnreadMessageId?: number | null;
}

/**
 * Maps the webapp JobChatController response into the JobChat shape expected by
 * frontend components. The webapp response nests job info under jobAssignmentInfo
 * and worker info under workerInfo.
 */
function mapBackendJobChat(chat: BackendJobChatResponse): JobChat {
  const info = chat.jobAssignmentInfo ?? {};
  return {
    chatId: chat.chatId,
    jobAssignmentId: info.jobAssignmentId ?? 0,
    jobAssignmentInfo: {
      eventName: info.eventName ?? '',
      jobId: info.jobId ?? 0,
      jobAssignmentId: info.jobAssignmentId ?? 0,
      start: info.start ?? '',
      end: info.end ?? '',
      position: info.position ?? '',
      address: info.address ?? {},
    },
    workerId: chat.workerInfo?.workerId ?? 0,
    workerFullName: chat.workerInfo?.workerFullName ?? '',
    memberId: 0,
    memberFullName: info.companyName ?? '',
    companyName: info.companyName ?? '',
    companyLogoUrl: info.companyLogoUrl ?? '',
    profileURLName: info.profileURLName,
    unreadMessages: chat.unreadMessages ?? 0,
    firstUnreadMessageId: chat.firstUnreadMessageId ?? null,
    lastMessageDateTime: chat.lastMessageDateTime ?? '',
  };
}

// ─── Counter Functions ──────────────────────────────────────────────────────

export async function getWorkerInboxIndicators(): Promise<WorkerInboxIndicators> {
  const url = 'worker/comhub/indicators';
  try {
    const response = await privateApi.get(url);
    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.errorMessage ||
          'Error fetching inbox indicators'
      );
    }
    throw error;
  }
}

// ─── Data Functions ─────────────────────────────────────────────────────────

export async function getWorkerDirectMessages(
  page: number,
  itemsPerPage: number,
  searchText?: string,
  dateRange?: { startDate: Date | undefined; endDate: Date | undefined },
  isUnread?: boolean,
  mode?: string
): Promise<DirectChatsResponse> {
  const baseUrl = 'worker/direct-chats';
  const queryParams = buildInboxQueryParams({
    page,
    itemsPerPage,
    searchText,
    dateRange,
    isUnread,
    mode,
  });
  const url = `${baseUrl}?${queryParams}`;
  try {
    const response = await privateApi.get(url);
    if (response.data && response.data.success === false) {
      throw new Error(
        response.data.message ||
          response.data.errorMessage ||
          'Error fetching direct messages'
      );
    }
    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.errorMessage ||
          'Error fetching direct messages'
      );
    }
    throw error;
  }
}

export async function getWorkerJobMessages(
  page: number,
  itemsPerPage: number,
  searchText?: string,
  dateRange?: { startDate: Date | undefined; endDate: Date | undefined },
  isUnread?: boolean,
  mode?: string
): Promise<JobChatResponse> {
  const baseUrl = 'worker/chat/job-chats';
  const queryParams = buildInboxQueryParams({
    page,
    itemsPerPage,
    searchText,
    dateRange,
    isUnread,
    mode,
  });
  const url = `${baseUrl}?${queryParams}`;
  try {
    const response = await privateApi.get(url);
    if (response.data && response.data.success === false) {
      throw new Error(
        response.data.message ||
          response.data.errorMessage ||
          'Error fetching job messages'
      );
    }
    const { data } = response;
    return {
      jobChats: (data.jobChats ?? []).map(mapBackendJobChat),
      pagination: data.pagination,
    };
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.errorMessage ||
          'Error fetching job messages'
      );
    }
    throw error;
  }
}

// ─── Todo Functions ─────────────────────────────────────────────────────────

export async function getWorkerTodos(): Promise<TodoResponse> {
  const url = 'worker/comhub/todos';
  try {
    const response = await privateApi.get(url);
    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.errorMessage ||
          'Error fetching todos'
      );
    }
    throw error;
  }
}

export async function getOtherNotifications(
  params: FetchNotificationsParams
): Promise<OtherNotificationResponse> {
  const queryParams = new URLSearchParams();
  if (params.offset != null) {
    queryParams.append(InboxQueryParamsEnum.OFFSET, params.offset.toString());
  }
  if (params.max != null) {
    queryParams.append(InboxQueryParamsEnum.MAX, params.max.toString());
  }
  queryParams.append(InboxQueryParamsEnum.MARK_AS_READ, 'false');
  if (params.dateFrom) {
    queryParams.append(InboxQueryParamsEnum.DATE_FROM, params.dateFrom);
  }
  if (params.dateTo) {
    queryParams.append(InboxQueryParamsEnum.DATE_TO, params.dateTo);
  }

  const url = `worker/comhub/notifications?${queryParams}`;
  try {
    const response = await privateApi.get(url);
    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.errorMessage ||
          'Error fetching notifications'
      );
    }
    throw error;
  }
}

export async function dismissNotification(id: number): Promise<void> {
  const url = `worker/comhub/notifications/${id}/dismiss`;
  try {
    await privateApi.post(url);
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.errorMessage ||
          'Could not dismiss notification'
      );
    }
    throw error;
  }
}

export async function markOtherNotificationRead(id: number): Promise<void> {
  const url = `worker/comhub/notifications/${id}/read`;
  try {
    await privateApi.post(url);
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.errorMessage ||
          'Could not mark notification as read'
      );
    }
    throw error;
  }
}
